import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

import { getDynamicPhoneNumber } from '../../services/constants';

// Import missing icon components
import { CheckCircle, Phone, Calculator, CreditCard } from 'lucide-react';

// Import components
import PaymentCalculator from './components/PaymentCalculator';
import PricingTiers from './components/PricingTiers';
import TestimonialSection from './components/TestimonialSection';
import Shield from '../../components/Shield';


const PaymentPlansPage = () => {
  const [fallbackPhone, setFallbackPhone] = useState('+19162263934');
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const dynamicPhone = getDynamicPhoneNumber();
    if (dynamicPhone) {
      setFallbackPhone(dynamicPhone);
    }
  }, []);

  const formatPhoneDisplay = (phone) => {
    if (!phone || typeof phone !== 'string') return '';
    const cleanPhone = phone?.replace('+1', '');
    if (!cleanPhone) return '';
    return cleanPhone?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  return (
    <>
      <Helmet>
        <title>Payment Plans - 0% Down Bail Bonds | Affordable Options | (916) 930-6312</title>
        <meta name="description" content="Flexible bail bond payment plans starting at $50 down. 0% down OAC available. Get instant quotes, approval in minutes. Licensed Sacramento bail bondsman." />
        <meta name="keywords" content="bail bond payment plans, 0% down bail bonds, affordable bail, Sacramento payment plans, flexible bail financing" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white overflow-hidden">
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 lg:py-24">
            <div className="text-center max-w-5xl mx-auto">
              <div className="inline-flex items-center bg-green-500/20 border border-green-400 text-green-100 px-6 py-2 rounded-full mb-8">
                <CheckCircle className="w-5 h-5 mr-2" />
                Approval in Minutes - No Hidden Fees
              </div>

              <h1 className="text-4xl lg:text-6xl font-black mb-6 leading-tight">
                Flexible Payment Plans
                <span className="block text-yellow-300">Starting at $50 Down</span>
              </h1>

              <p className="text-xl lg:text-2xl mb-8 text-blue-100 leading-relaxed">
                Don't let money keep your loved one in jail. We work with any budget to get them home tonight.
              </p>

              <div className="flex flex-col lg:flex-row gap-4 justify-center items-center mb-12">
                <a 
                  href="tel:9162563468"
                  className="group bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-2xl font-black text-xl transition-all transform hover:scale-105 shadow-2xl flex items-center"
                >
                  <Phone className="w-6 h-6 mr-3 group-hover:animate-pulse" />
                  Get Instant Quote
                </a>
                
                <div className="text-blue-200 text-lg font-medium">
                  🔒 Secure • Licensed • Bonded
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Tiers */}
        <PricingTiers onSelectPlan={setSelectedPlan} />

        {/* Interactive Calculator */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Calculate Your Payment Plan
              </h2>
              <p className="text-xl text-gray-600">
                Get instant monthly payment estimates with no obligation
              </p>
            </div>
            
            <PaymentCalculator />
          </div>
        </section>

        {/* No Hidden Fees Guarantee */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Our No Hidden Fees Guarantee
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="text-center p-8 bg-green-50 rounded-2xl border border-green-200">
                <Shield className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600">What we quote is exactly what you pay. No surprise fees, no hidden costs.</p>
              </div>
              
              <div className="text-center p-8 bg-blue-50 rounded-2xl border border-blue-200">
                <Calculator className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Fixed Monthly Payments</h3>
                <p className="text-gray-600">Your payment amount never changes. Budget with confidence.</p>
              </div>
              
              <div className="text-center p-8 bg-purple-50 rounded-2xl border border-purple-200">
                <CreditCard className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Approval in Minutes</h3>
                <p className="text-gray-600">Quick approval process gets you the help you need fast.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Plan Examples */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Real Payment Plan Examples
              </h2>
              <p className="text-xl text-gray-600">
                See how affordable bail can be with our flexible options
              </p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {[
                {
                  bailAmount: "$5,000",
                  bondFee: "$500",
                  downPayment: "$50",
                  monthlyPayment: "$75",
                  duration: "6 months",
                  type: "Budget Plan"
                },
                {
                  bailAmount: "$10,000", 
                  bondFee: "$1,000",
                  downPayment: "$0",
                  monthlyPayment: "$167",
                  duration: "6 months",
                  type: "0% Down (OAC)"
                },
                {
                  bailAmount: "$25,000",
                  bondFee: "$2,500", 
                  downPayment: "$250",
                  monthlyPayment: "$375",
                  duration: "6 months",
                  type: "Standard Plan"
                }
              ]?.map((example, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                  <div className="text-center mb-6">
                    <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold mb-4">
                      {example?.type}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Bail: {example?.bailAmount}</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bond Fee:</span>
                      <span className="font-bold">{example?.bondFee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Down Payment:</span>
                      <span className="font-bold text-green-600">{example?.downPayment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Payment:</span>
                      <span className="font-bold text-blue-600">{example?.monthlyPayment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-bold">{example?.duration}</span>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <a 
                      href="tel:9162563468"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold text-center block transition-colors"
                    >
                      Apply for This Plan
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <TestimonialSection />

        {/* Quick Application CTA */}
        <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-black mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8">
              Call now for instant approval and get your loved one home tonight
            </p>
            
            <div className="flex flex-col lg:flex-row gap-4 justify-center items-center">
              <a 
                href="tel:9162563468"
                className="bg-white text-green-600 px-12 py-6 rounded-2xl font-black text-2xl hover:bg-green-50 transition-all transform hover:scale-105 shadow-2xl flex items-center"
              >
                <Phone className="w-8 h-8 mr-4" />
                (916) 256-3468
              </a>
              
              <div className="text-green-100 font-bold text-lg">
                Licensed agents available 24/7
              </div>
            </div>
            
            <div className="mt-8 bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="grid md:grid-cols-2 gap-6 text-lg">
                <div className="flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 mr-3" />
                  <span>Approval in Minutes</span>
                </div>
                <div className="flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 mr-3" />
                  <span>Payment Plans from $50</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PaymentPlansPage;